import { authConfig, isAdmin } from '../store/UserStore';
import axios from 'axios';
import { Session, SessionHistoryDTO, SessionHistoryPdfParams, Therapist } from '../model/Types';
import { appLanguage } from '../store/LanguageStore';

// During react-snap prerender (headless Chromium at build time), relative URLs
// would hit react-snap's internal static server which has no backend. In that
// case we use the public domain so requests go through nginx → Spring Boot.
// In the real browser we keep an empty base so axios uses relative URLs.
const isPrerender =
    typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);

//export const BALANCE_PATH_BASE_URL = 'http://localhost:8081';
export const BALANCE_PATH_BASE_URL = isPrerender ? 'https://balancepath.space' : '';

export const addTherapist = (therapist: Therapist) => {
    return axios.post(BALANCE_PATH_BASE_URL + '/admin/addNewTherapist', therapist, {
        headers: {
            Authorization: `Bearer ${authConfig.value}`,
            'Accept-Language': appLanguage.value?.toLowerCase(),
        },
    });
};

export const updateTherapist = (therapist: Therapist) => {
    return axios.put(
        BALANCE_PATH_BASE_URL + `/admin/updateNewTherapist/${therapist.id}`,
        therapist,
        {
            headers: {
                Authorization: `Bearer ${authConfig.value}`,
                'Accept-Language': appLanguage.value?.toLowerCase(),
            },
        },
    );
};

export const uploadTherapistAvatar = (avatar: FormData | undefined) => {
    return axios.post(BALANCE_PATH_BASE_URL + '/admin/uploadTherapistPhoto', avatar, {
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Accept-Language': appLanguage.value?.toLowerCase(),
        },
    });
};

export const activateTherapistById = (id: number) => {
    return axios.put(
        `${BALANCE_PATH_BASE_URL}/admin/activateTherapist/${id}`,
        {},
        {
            headers: {
                Authorization: `Bearer ${authConfig.value}`,
                'Accept-Language': appLanguage.value?.toLowerCase(),
            },
        },
    );
};

export const deactivateTherapistById = (id: number) => {
    return axios.put(
        `${BALANCE_PATH_BASE_URL}/admin/deactivateTherapist/${id}`,
        {},
        {
            headers: {
                Authorization: `Bearer ${authConfig.value}`,
                'Accept-Language': appLanguage.value?.toLowerCase(),
            },
        },
    );
};

export const inactivateTherapistById = (id: number) => {
    return axios.put(
        `${BALANCE_PATH_BASE_URL}/admin/inactivateTherapist/${id}`,
        {},
        {
            headers: {
                Authorization: `Bearer ${authConfig.value}`,
                'Accept-Language': appLanguage.value?.toLowerCase(),
            },
        },
    );
};

export const deleteTherapistById = (id: number) => {
    return axios.delete(`${BALANCE_PATH_BASE_URL}/admin/deleteTherapist/${id}`, {
        headers: {
            Authorization: `Bearer ${authConfig.value}`,
            'Accept-Language': appLanguage.value?.toLowerCase(),
        },
    });
};

export const fetchAllTherapists = () => {
    if (isAdmin.value) {
        return axios.get<Therapist[]>(BALANCE_PATH_BASE_URL + '/admin/therapist/all', {
            headers: {
                Authorization: `Bearer ${authConfig.value}`,
                'Accept-Language': appLanguage.value?.toLowerCase(),
            },
        });
    } else {
        return axios.get<Therapist[]>(BALANCE_PATH_BASE_URL + '/therapist/all', {
            headers: {
                'Accept-Language': appLanguage.value?.toLowerCase(),
            },
        });
    }
};

export const getHistory = () => {
    return axios.get<Session[]>(`${BALANCE_PATH_BASE_URL}/history/all`, {
        headers: {
            Authorization: `Bearer ${authConfig.value}`,
            'Accept-Language': appLanguage.value?.toLowerCase(),
        },
    });
};

export const getSessionHistory = () => {
    return axios.get<SessionHistoryDTO[]>(`${BALANCE_PATH_BASE_URL}/sessions-history/getAll`, {
        headers: {
            Authorization: `Bearer ${authConfig.value}`,
            'Accept-Language': appLanguage.value?.toLowerCase(),
        },
        params: { adminTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    });
};

export const exportSessionHistory = async (params: SessionHistoryPdfParams) => {
    const response = await axios.post(`${BALANCE_PATH_BASE_URL}/sessions-history/export`, params, {
        headers: {
            Authorization: `Bearer ${authConfig.value}`,
            'Accept-Language': appLanguage.value?.toLowerCase(),
        },
        responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${params.therapistName} Session History.pdf`); // Set desired filename
    document.body.appendChild(link);
    link.click();
    link.remove();
};
